import {
  Perks,
  PerkTypes,
  StandardPerk,
  StandardPerks,
  TradeoffPerk,
  TradeoffPerks,
  UltimatePerk,
  UltimatePerks,
  UltimateWeapons,
} from 'types/perks';
import { States } from 'types/states';
import { random, rollPercent } from 'utils/random';
import { getEnemyCountsForWave } from './enemyCount';
import formatCoins from './formatCoins';
import formatPerk from './formatPerk';

const BOSS_COOLDOWN = 12;
const NORMAL_COOLDOWN = 9;
const BASE_WAVE_TIME = 26;

const getWaveTime = (isBossWave: boolean, waveAccelerator: number) => {
  const baseCooldown = isBossWave ? BOSS_COOLDOWN : NORMAL_COOLDOWN;
  const cooldown = baseCooldown * (1 - waveAccelerator / 100);
  return BASE_WAVE_TIME + cooldown;
};

const getRealWaveTime = (
  isBossWave: boolean,
  gameSpeed: number,
  standardResearch: number,
  hasGameSpeedPerk: boolean,
  introSprint: boolean,
  waveAccelerator: number,
) => {
  const displayedGameSpeed =
    gameSpeed + (hasGameSpeedPerk ? 1 + standardResearch / 100 : 0);
  const waveTime = getWaveTime(isBossWave, waveAccelerator);
  let realGameSpeed = 1;
  // This should probably be mapped instead of all the branches, but we don't have data for uncommon things like 2.73 speed
  // so doing thresholds this way buckets them together
  if (displayedGameSpeed <= 1) {
    realGameSpeed = introSprint ? 6.07999992371 : 1.07000005245;
  } else if (displayedGameSpeed <= 1.5) {
    realGameSpeed = introSprint ? 9.11999988556 : 1.60500001907;
  } else if (displayedGameSpeed <= 2) {
    realGameSpeed = introSprint ? 12.15999984741 : 2;
  } else if (displayedGameSpeed <= 2.5) {
    realGameSpeed = introSprint ? 15.19999980927 : 2.375;
  } else if (displayedGameSpeed <= 3) {
    realGameSpeed = introSprint ? 18.23999977112 : 2.75999999046;
  } else if (displayedGameSpeed <= 3.5) {
    realGameSpeed = introSprint ? 21.28000068665 : 3.07999992371;
  } else if (displayedGameSpeed <= 4) {
    realGameSpeed = introSprint ? 24.31999969482 : 3.3599998951;
  } else if (displayedGameSpeed <= 4.5) {
    realGameSpeed = introSprint ? 27.36000061035 : 3.69000005722;
  } else if (displayedGameSpeed <= 5) {
    realGameSpeed = introSprint ? 30.39999961853 : 4;
  } else if (displayedGameSpeed <= 5.5) {
    realGameSpeed = 4.40000009537;
  } else if (displayedGameSpeed <= 6) {
    realGameSpeed = 4.80000019073;
  } else if (displayedGameSpeed <= 6.01) {
    realGameSpeed = 4.80800008774;
  } else if (displayedGameSpeed <= 6.02) {
    realGameSpeed = 4.81599998474;
  } else if (displayedGameSpeed <= 6.03) {
    realGameSpeed = 4.82399988174;
  } else if (displayedGameSpeed <= 6.04) {
    realGameSpeed = 4.83200025558;
  } else if (displayedGameSpeed <= 6.05) {
    realGameSpeed = 4.84000015259;
  } else if (displayedGameSpeed <= 6.06) {
    realGameSpeed = 4.84800004959;
  } else if (displayedGameSpeed <= 6.07) {
    realGameSpeed = 4.85600042343;
  } else if (displayedGameSpeed <= 6.08) {
    realGameSpeed = 4.8639998436;
  } else if (displayedGameSpeed <= 6.09) {
    realGameSpeed = 4.87200021744;
  } else if (displayedGameSpeed <= 6.1) {
    realGameSpeed = 4.88000011444;
  } else if (displayedGameSpeed <= 6.11) {
    realGameSpeed = 4.88800001144;
  } else if (displayedGameSpeed <= 6.12) {
    realGameSpeed = 4.89599990845;
  } else if (displayedGameSpeed <= 6.13) {
    realGameSpeed = 4.90400028229;
  } else if (displayedGameSpeed <= 6.14) {
    realGameSpeed = 4.91200017929;
  } else if (displayedGameSpeed <= 6.15) {
    realGameSpeed = 4.92000007629;
  } else if (displayedGameSpeed <= 6.16) {
    realGameSpeed = 4.9279999733;
  } else if (displayedGameSpeed <= 6.17) {
    realGameSpeed = 4.93600034714;
  } else if (displayedGameSpeed <= 6.18) {
    realGameSpeed = 4.9439997673;
  } else if (displayedGameSpeed <= 6.19) {
    realGameSpeed = 4.95200014114;
  } else if (displayedGameSpeed <= 6.2) {
    realGameSpeed = 4.96000003815;
  } else if (displayedGameSpeed <= 6.21) {
    realGameSpeed = 4.96799993515;
  } else if (displayedGameSpeed <= 6.22) {
    realGameSpeed = 4.97600030899;
  } else if (displayedGameSpeed <= 6.23) {
    realGameSpeed = 4.98400020599;
  } else if (displayedGameSpeed <= 6.24) {
    realGameSpeed = 4.992000103;
  } else if (displayedGameSpeed <= 6.25) {
    realGameSpeed = 5;
  }
  return waveTime / realGameSpeed;
};

export type PerkQuantity = {
  [key in PerkTypes]?: number;
};
export type Quantity = {
  [key: string]: number;
};

export type UltimateWeaponUnlocks = {
  [UltimateWeapons.SMART_MISSILES]: boolean;
  [UltimateWeapons.CHAIN_LIGHTNING]: boolean;
  [UltimateWeapons.CHRONO_FIELD]: boolean;
  [UltimateWeapons.GOLDEN_TOWER]: boolean;
  [UltimateWeapons.DEATH_WAVE]: boolean;
  [UltimateWeapons.POISON_SWAMP]: boolean;
  [UltimateWeapons.INNER_LAND_MINES]: boolean;
  [UltimateWeapons.BLACK_HOLE]: boolean;
  [UltimateWeapons.SPOTLIGHT]: boolean;
};

export type WaveStats = {
  coins: number;
  time: number;
  cph: number;
  waveSkips: number;
  perkMultiplier: number;
  hasTradeoffCoins: number;
  hasDamageTradeoff: number;
  hasAllCoinBonuses: number;
  hasAllNeededPerks: number;
  hasGoldenTower: number;
  hasGameSpeed: number;
  extraOrbs: number;
  defenseBonuses: number;
  hpBonuses: number;
  basicCoinBonuses: number;
  noMoreOptions: number;
  hasFirstPerkReduction: number;
  hasSecondPerkReduction: number;
  hasThirdPerkReduction: number;
  perkCount: number;
};
export type PerkWaveStats = {
  wave: number;
  has: number;
};
export type Stats = {
  finalStats: {
    perkCount: number;
    perkMultiplier: number;
    basicCoinBonuses: number;
    hpBonuses: number;
    defenseBonuses: number;
    extraOrbs: number;
  };
  perkWaveStats: {
    gameSpeedWave: PerkWaveStats;
    goldenTowerWave: PerkWaveStats;
    coinBonusesWave: PerkWaveStats;
    tradeoffCoinWave: PerkWaveStats;
    allCoinBonuses: PerkWaveStats;
    allNeededPerks: PerkWaveStats;
    noMoreOptions: PerkWaveStats;
    damageDamageWave: PerkWaveStats;
    firstPerkReduction: PerkWaveStats;
    secondPerkReduction: PerkWaveStats;
    thirdPerkReduction: PerkWaveStats;
  };
  waves: {
    [wave: number]: WaveStats;
  };
};

export type Results = {
  count: number;
  stats: Stats;
};

const ULTIMATE_CHANCE = 20;
const TRADEOFF_CHANCE = 15;

const calculateNextPerkWave = ({
  perkCount,
  waveReductionResearch,
  reductionPercent,
}: {
  perkCount: number;
  waveReductionResearch: number;
  reductionPercent: number;
}) => {
  let quantity = 0;
  let nextWave = 0;
  while (quantity <= perkCount) {
    let offset = 0;
    if (quantity >= 20 && quantity < 30) offset = 50;
    if (quantity >= 30 && quantity < 40) offset = 100;
    if (quantity >= 40) offset = 150;
    quantity += 1;
    nextWave += Math.floor(
      (200 + offset - waveReductionResearch) * (1 - reductionPercent / 100),
    );
  }
  return nextWave;
};

// TODO - helpers for scaling + converting between percents + handling 2 decimal places
const calculatePerkCoinMultiplier = (
  perks: PerkQuantity,
  standardResearch: number,
  tradeoffResearch: number,
) => {
  const coinBonusQuantity = perks[StandardPerk.COIN_BONUSES] || 0;
  const tradeoffQuantity = perks[TradeoffPerk.COINS_HEALTH] || 0;
  const basicCoinMultiplier =
    coinBonusQuantity > 0
      ? (1 + (Perks[StandardPerk.COIN_BONUSES].value - 1) * coinBonusQuantity) *
        (1 + standardResearch / 100)
      : 1;
  const tradeoffMultiplier =
    tradeoffQuantity > 0
      ? (1 + (Perks[TradeoffPerk.COINS_HEALTH].value - 1) * tradeoffQuantity) *
        (1 + tradeoffResearch / 100)
      : 1;
  return Math.round(basicCoinMultiplier * tradeoffMultiplier * 100) / 100;
};

// TODO - helpers ^^
const calculateReductionPercent = (
  perks: PerkQuantity,
  standardResearch: number,
) => {
  const reductionQuantity = perks[StandardPerk.PERK_WAVE_REQUIREMENT] || 0;
  const reductionValue =
    reductionQuantity > 0
      ? Math.floor(
          Perks[StandardPerk.PERK_WAVE_REQUIREMENT].value *
            reductionQuantity *
            (1 + standardResearch / 100) *
            100,
        ) / 100
      : 0;
  return reductionValue;
};

const selectByPriority = (
  perks: PerkQuantity,
  perkChoices: PerkTypes[],
  perkPriorityList: PerkTypes[],
  neverPickPerks: PerkTypes[],
) => {
  const availableChoices = perkChoices
    .filter(choice => !neverPickPerks.includes(choice))
    .sort((firstChoice, secondChoice) => {
      const firstChoicePosition = perkPriorityList.findIndex(
        perk => perk === firstChoice,
      );
      const secondChoicePosition = perkPriorityList.findIndex(
        perk => perk === secondChoice,
      );
      if (firstChoicePosition >= 0 && secondChoicePosition >= 0)
        return firstChoicePosition - secondChoicePosition;
      if (firstChoicePosition >= 0) return -1;
      if (secondChoicePosition >= 0) return 1;

      const firstLeft = Perks[firstChoice].maxLevel - (perks[firstChoice] || 0);
      const secondLeft =
        Perks[secondChoice].maxLevel - (perks[secondChoice] || 0);

      // burn whichever one has the fewest remaining
      return firstLeft - secondLeft;
    });
  if (availableChoices.length > 0) return availableChoices[0];
  return undefined;
};

const getPerkChoicesForCategory = (
  perks: PerkQuantity,
  category: typeof TradeoffPerks | typeof UltimatePerks | typeof StandardPerks,
) =>
  Object.keys(category).filter(key => {
    const perk = category[key];
    const currentAmount = perks[key] || 0;
    return currentAmount < perk.maxLevel;
  });

const getUltimateWeaponChoices = (
  perks: PerkQuantity,
  ultimateWeapons: UltimateWeaponUnlocks,
) =>
  getPerkChoicesForCategory(perks, UltimatePerks).filter(perk =>
    Object.keys(ultimateWeapons)
      .filter(key => ultimateWeapons[key])
      .find(uw => uw === perk),
  );

const getPerkChoices = ({
  perkCount,
  firstPerkChoice,
  perks,
  perkOptionResearch,
  bannedPerks,
  ultimateWeapons,
}: {
  perkCount: number;
  firstPerkChoice?: StandardPerk;
  perks: PerkQuantity;
  perkOptionResearch: number;
  bannedPerks: PerkTypes[];
  ultimateWeapons: UltimateWeaponUnlocks;
}) => {
  const choices: (PerkTypes | null)[] = [];

  const hasAllUltimates = Object.values(ultimateWeapons).every(Boolean);

  const canPick = (perk: PerkTypes) =>
    // don't pick banned perks
    !bannedPerks.includes(perk) &&
    // don't pick perks already selected as options
    !choices.includes(perk) &&
    // can't pick unlock ultimate if we have all UW unlocked
    (!hasAllUltimates || perk !== StandardPerk.UNLOCK_ULTIMATE_WEAPON);

  if (perkCount === 0 && firstPerkChoice && perkOptionResearch > 0)
    choices.push(firstPerkChoice);

  while (choices.length < perkOptionResearch) {
    let category:
      | typeof StandardPerk
      | typeof UltimatePerk
      | typeof TradeoffPerk;

    // Figure out what category of perk we want
    const rng = random(0, 100);
    if (rng < ULTIMATE_CHANCE) {
      category = UltimatePerk;
    } else if (rng < ULTIMATE_CHANCE + TRADEOFF_CHANCE) {
      category = TradeoffPerk;
    } else {
      category = StandardPerk;
    }

    // get our options for the category with fallback
    let options;
    if (category === UltimatePerk) {
      options = getUltimateWeaponChoices(perks, ultimateWeapons);
      options = options.filter(canPick);
      // fall back to standard
      if (!options.length)
        options = getPerkChoicesForCategory(perks, StandardPerks);
    } else if (category === TradeoffPerk) {
      options = getPerkChoicesForCategory(perks, TradeoffPerks);
      options = options.filter(canPick);
      // fall back to standard
      if (!options.length)
        options = getPerkChoicesForCategory(perks, StandardPerks);
    } else {
      options = getPerkChoicesForCategory(perks, StandardPerks);
    }
    options = options.filter(canPick);

    // V 0.15 upgrades categories when out of standards. Not sure how, so for now just upgrade in order.
    if (!options.length) {
      options = getUltimateWeaponChoices(perks, ultimateWeapons);
      options = options.filter(canPick);
      if (!options.length) {
        options = getPerkChoicesForCategory(perks, TradeoffPerks);
        options = options.filter(canPick);
      }
    }
    // select a random option, or null if none available
    if (options.length) {
      const index = random(0, options.length);
      const choice = options[index];
      choices.push(choice);
    } else {
      // no valid options, empty slot
      choices.push(null);
    }
  }
  // filter out nulls
  return choices.filter(choice => !!choice) as PerkTypes[];
};

export const simulate = ({
  maxWave,
  smartMissilesUnlocked,
  deathWaveUnlocked,
  chainLightningUnlocked,
  chronoFieldUnlocked,
  goldenTowerUnlocked,
  innerLandMinesUnlocked,
  poisonSwampUnlocked,
  blackHoleUnlocked,
  spotlightUnlocked,

  standardResearch,
  tradeoffResearch,
  waveReductionResearch,
  perkOptionResearch,

  coinBonuses,
  coinsPerKill,
  coinsPerWave,
  goldenTowerBonus: stockBonus,
  goldenTowerDuration: stockDuration,
  goldenTowerCooldown: stockCooldown,
  blackHoleCoverage,
  blackHoleCoins,
  blackHoleDuration,
  blackHoleCooldown,
  criticalCoin,

  spotlightCoins,
  spotlightQuantity,
  spotlightAngle,

  firstPerkChoice,
  bannedPerks,
  perkPriorityList,
  neverPickPerks,

  gameSpeed,
  waveSkip,
  introSprint,

  waveAccelerator,
  autopick,

  verbose, // whether or not to record a text log of what happened
}: Omit<States, 'simulationCount'> & { verbose?: boolean }) => {
  let currentWave = 0;
  let goldenTowerBonus = stockBonus;
  let goldenTowerDuration = stockDuration;
  let goldenTowerCooldown = stockCooldown;

  const spotlightCoverage = spotlightUnlocked
    ? (spotlightAngle * spotlightQuantity) / 360
    : 0;
  const spotlightMultiplier = // Average multiplier
    spotlightUnlocked
      ? 1 * (1 - spotlightCoverage) + spotlightCoins * spotlightCoverage
      : 1;
  const ultimateWeapons: UltimateWeaponUnlocks = {
    [UltimateWeapons.SMART_MISSILES]: smartMissilesUnlocked,
    [UltimateWeapons.CHAIN_LIGHTNING]: chainLightningUnlocked,
    [UltimateWeapons.CHRONO_FIELD]: chronoFieldUnlocked,
    [UltimateWeapons.GOLDEN_TOWER]: goldenTowerUnlocked,
    [UltimateWeapons.DEATH_WAVE]: deathWaveUnlocked,
    [UltimateWeapons.POISON_SWAMP]: poisonSwampUnlocked,
    [UltimateWeapons.INNER_LAND_MINES]: innerLandMinesUnlocked,
    [UltimateWeapons.BLACK_HOLE]: blackHoleUnlocked,
    [UltimateWeapons.SPOTLIGHT]: spotlightUnlocked,
  };
  let totalCoins = 0;
  let previousCoins = 0;
  const stats: Stats = {
    waves: {},
    perkWaveStats: {
      gameSpeedWave: {
        wave: 0,
        has: 0,
      },
      goldenTowerWave: {
        wave: 0,
        has: 0,
      },
      coinBonusesWave: {
        wave: 0,
        has: 0,
      },
      tradeoffCoinWave: {
        wave: 0,
        has: 0,
      },
      allCoinBonuses: {
        wave: 0,
        has: 0,
      },
      allNeededPerks: {
        wave: 0,
        has: 0,
      },
      noMoreOptions: {
        wave: 0,
        has: 0,
      },
      damageDamageWave: {
        wave: 0,
        has: 0,
      },
      firstPerkReduction: {
        wave: 0,
        has: 0,
      },
      secondPerkReduction: {
        wave: 0,
        has: 0,
      },
      thirdPerkReduction: {
        wave: 0,
        has: 0,
      },
    },
    finalStats: {
      basicCoinBonuses: 0,
      hpBonuses: 0,
      defenseBonuses: 0,
      extraOrbs: 0,
      perkCount: 0,
      perkMultiplier: 1,
    },
  };
  const timers = {
    total: 0,
    goldenTowerActive: 0,
    goldenTowerCooldown,
    blackHoleActive: 0,
    blackHoleCooldown,
  };
  let perkCoinMultiplier = 1;
  let reductionPercent = 0;
  let perkCount = 0;
  let waveSkips = 0;
  const perks = {};
  const log: string[] = [];
  let nextPerkWave = calculateNextPerkWave({
    perkCount,
    waveReductionResearch,
    reductionPercent,
  });
  let blockedOnPerks = false;
  let currentGameSpeed = gameSpeed;
  if (verbose) log.push('Simulation Log:');
  while (currentWave < maxWave) {
    // Start next wave
    currentWave += 1;
    const isIntroSprinting = currentWave <= introSprint;
    if (currentWave > 1 && waveSkip > 0 && rollPercent(waveSkip)) {
      if (verbose) log.push(`Skipping wave ${currentWave}`);
      let nextCoins = previousCoins * 1.1;
      if (isIntroSprinting) nextCoins *= 0.2;
      totalCoins += nextCoins;
      previousCoins = nextCoins;
      waveSkips += 1;
      if (currentWave % 100 === 0) {
        const cph = Math.floor((totalCoins * 3600) / timers.total);
        if (verbose) {
          log.push(
            `Stats for ${currentWave}: Coins ${formatCoins(
              totalCoins,
            )}, CPH ${formatCoins(
              cph,
            )}, perk bonus: ${perkCoinMultiplier}, currentGameSpeed: ${currentGameSpeed}`,
          );
        }
        stats.waves[currentWave] = {
          coins: totalCoins,
          time: Math.floor(timers.total),
          cph,
          waveSkips,
          perkMultiplier: perkCoinMultiplier,
          hasTradeoffCoins: stats.perkWaveStats.tradeoffCoinWave.has,
          hasDamageTradeoff: stats.perkWaveStats.damageDamageWave.has,
          hasAllCoinBonuses: stats.perkWaveStats.coinBonusesWave.has,
          hasAllNeededPerks: stats.perkWaveStats.allNeededPerks.has,
          hasGoldenTower: stats.perkWaveStats.goldenTowerWave.has,
          hasGameSpeed: stats.perkWaveStats.gameSpeedWave.has,
          extraOrbs: perks[StandardPerk.ORBS],
          defenseBonuses: perks[StandardPerk.DEFENSE_PERCENT],
          hpBonuses: perks[StandardPerk.MAX_HEALTH],
          basicCoinBonuses: perks[StandardPerk.COIN_BONUSES],
          noMoreOptions: stats.perkWaveStats.noMoreOptions.has,
          hasFirstPerkReduction: stats.perkWaveStats.firstPerkReduction.has,
          hasSecondPerkReduction: stats.perkWaveStats.secondPerkReduction.has,
          hasThirdPerkReduction: stats.perkWaveStats.thirdPerkReduction.has,
          perkCount,
        };
      }
      continue;
    }
    const isBossWave = currentWave % 10 === 0;
    const currentWaveTime = getWaveTime(isBossWave, waveAccelerator);

    // Select perks and re-math bonuses
    while (currentWave >= nextPerkWave && !blockedOnPerks) {
      if (verbose) log.push(`Picking perk on ${currentWave}`);
      const perkChoices = getPerkChoices({
        perkCount,
        firstPerkChoice,
        perks,
        perkOptionResearch,
        bannedPerks,
        ultimateWeapons,
      });
      if (verbose)
        perkChoices.forEach((choice, i) =>
          log.push(
            `Choice ${i + 1}: ${formatPerk(
              choice,
              standardResearch,
              tradeoffResearch,
            )}`,
          ),
        );
      const perkChoice = autopick
        ? perkChoices[0]
        : selectByPriority(
            perks,
            perkChoices,
            perkPriorityList,
            neverPickPerks,
          );
      if (perkChoice) {
        if (verbose)
          log.push(
            `Picked perk ${formatPerk(
              perkChoice,
              standardResearch,
              tradeoffResearch,
            )}`,
          );
        perks[perkChoice] = (perks[perkChoice] || 0) + 1;
        perkCount += 1;
        nextPerkWave = calculateNextPerkWave({
          perkCount,
          waveReductionResearch,
          reductionPercent,
        });
        if (perkChoice === StandardPerk.PERK_WAVE_REQUIREMENT) {
          reductionPercent = calculateReductionPercent(perks, standardResearch);
          nextPerkWave = calculateNextPerkWave({
            perkCount,
            waveReductionResearch,
            reductionPercent,
          });
          if (perks[StandardPerk.PERK_WAVE_REQUIREMENT] === 1) {
            stats.perkWaveStats.firstPerkReduction = {
              wave: currentWave,
              has: 1,
            };
          }
          if (perks[StandardPerk.PERK_WAVE_REQUIREMENT] === 2) {
            stats.perkWaveStats.secondPerkReduction = {
              wave: currentWave,
              has: 1,
            };
          }
          if (perks[StandardPerk.PERK_WAVE_REQUIREMENT] === 3) {
            stats.perkWaveStats.thirdPerkReduction = {
              wave: currentWave,
              has: 1,
            };
          }
        }
        if (perkChoice === UltimatePerk.GOLDEN_TOWER) {
          stats.perkWaveStats.goldenTowerWave = {
            wave: currentWave,
            has: 1,
          };
          goldenTowerBonus *= 1.5;
        }
        if (perkChoice === StandardPerk.COIN_BONUSES) {
          if (perks[StandardPerk.COIN_BONUSES] === 5)
            stats.perkWaveStats.coinBonusesWave = {
              wave: currentWave,
              has: 1,
            };
          perkCoinMultiplier = calculatePerkCoinMultiplier(
            perks,
            standardResearch,
            tradeoffResearch,
          );
        }
        if (perkChoice === TradeoffPerk.COINS_HEALTH) {
          stats.perkWaveStats.tradeoffCoinWave = {
            wave: currentWave,
            has: 1,
          };
          perkCoinMultiplier = calculatePerkCoinMultiplier(
            perks,
            standardResearch,
            tradeoffResearch,
          );
        }
        if (perkChoice === StandardPerk.INCREASE_GAME_SPEED) {
          stats.perkWaveStats.gameSpeedWave = {
            wave: currentWave,
            has: 1,
          };
          currentGameSpeed += 1 + standardResearch / 100;
        }
        if (perkChoice === TradeoffPerk.DAMAGE_DAMAGE) {
          stats.perkWaveStats.damageDamageWave = {
            wave: currentWave,
            has: 1,
          };
        }
        if (perkChoice === StandardPerk.UNLOCK_ULTIMATE_WEAPON) {
          const uwChoices = Object.keys(ultimateWeapons).filter(
            uw => !ultimateWeapons[uw],
          );
          if (uwChoices.length > 0) {
            const uwChoice = random(0, uwChoices.length);
            const uw = uwChoices[uwChoice];
            if (verbose) log.push(`Unlocked Ultimate Weapon ${uw}`);
            ultimateWeapons[uw] = true;
            if (uw === UltimateWeapons.GOLDEN_TOWER) {
              goldenTowerBonus = 9;
              goldenTowerDuration = 30;
              goldenTowerCooldown = 230;
              timers.goldenTowerActive = 0;
              timers.goldenTowerCooldown = 230;
            } else if (uw === UltimateWeapons.BLACK_HOLE) {
              // don't do anything with BH cuz it doesn't give us coins without research
            } else if (uw === UltimateWeapons.SPOTLIGHT) {
              // don't do anything with SPOTLIGHT cuz it doesn't give us coins without research
            }
          } else if (verbose) {
            log.push(`No UWs available to unlock.`);
            log.push(
              `I'm not sure what happens if you have them all, so treating it as a noop.`,
            );
            log.push(
              `If its supposed to not be selectable, use an extra ban on it.`,
            );
          }
        }
        if (
          perks[StandardPerk.COIN_BONUSES] === 5 &&
          perks[TradeoffPerk.COINS_HEALTH] &&
          perks[UltimatePerk.GOLDEN_TOWER]
        ) {
          if (verbose && !stats.perkWaveStats.allCoinBonuses.has)
            log.push('All coin bonuses picked');
          if (!stats.perkWaveStats.allCoinBonuses.has)
            stats.perkWaveStats.allCoinBonuses = {
              wave: currentWave,
              has: 1,
            };
          if (
            perks[StandardPerk.INCREASE_GAME_SPEED] &&
            perks[TradeoffPerk.DAMAGE_DAMAGE] &&
            perks[StandardPerk.DEFENSE_PERCENT] === 5 &&
            perks[StandardPerk.MAX_HEALTH] === 5 &&
            perks[StandardPerk.ORBS] === 2
          ) {
            if (verbose && !stats.perkWaveStats.allNeededPerks.has)
              log.push('All needed perks picked');
            if (!stats.perkWaveStats.allNeededPerks.has)
              stats.perkWaveStats.allNeededPerks = {
                wave: currentWave,
                has: 1,
              };
          }
        }
      } else {
        // No more options or refused to pick, stop rolling
        if (verbose) log.push('No more selectable options');
        if (!stats.perkWaveStats.noMoreOptions.has)
          stats.perkWaveStats.noMoreOptions = {
            wave: currentWave,
            has: 1,
          };
        blockedOnPerks = true;
      }
    }

    // calculate enemies
    const currentWaveEnemies = getEnemyCountsForWave(currentWave);
    const coinsFromEnemies = {
      basic: Math.floor(currentWaveEnemies.basic * (criticalCoin / 100)),
      fast: Math.floor(2 * currentWaveEnemies.fast),
      tank: Math.floor(4 * currentWaveEnemies.tank),
      ranged: Math.floor(2 * currentWaveEnemies.ranged),
      boss: Math.floor(5 * currentWaveEnemies.boss),
    };

    const baseCoinsForWave =
      coinsFromEnemies.basic +
      coinsFromEnemies.fast +
      coinsFromEnemies.tank +
      coinsFromEnemies.ranged +
      coinsFromEnemies.boss;

    const scaledBaseCoinsForWave =
      baseCoinsForWave *
      coinBonuses *
      coinsPerKill *
      perkCoinMultiplier *
      spotlightMultiplier;

    // calculate next start/stop times of GT and BH. 0 start time if currently active
    const gtStartTime =
      timers.goldenTowerActive > 0 ? 0 : timers.goldenTowerCooldown;
    const gtStopTime =
      timers.goldenTowerActive > 0
        ? timers.goldenTowerActive
        : timers.goldenTowerCooldown + goldenTowerDuration;
    const bhStartTime =
      timers.blackHoleActive > 0 ? 0 : timers.blackHoleCooldown;
    const bhStopTime =
      timers.blackHoleActive > 0
        ? timers.blackHoleActive
        : timers.blackHoleCooldown + blackHoleDuration;
    // bh has an edge case at max uptime where it can start a wave active, stop, and restart in the same wave
    const bhRestartTime =
      timers.blackHoleActive > 0
        ? timers.blackHoleCooldown
        : timers.blackHoleCooldown + blackHoleCooldown;
    const bhRestartStopTime = bhRestartTime + blackHoleDuration;

    let coinsFromGoldenTower = 0;
    let coinsFromBlackHole = 0;
    let coinsFromBoth = 0;
    let coinsFromNeither = scaledBaseCoinsForWave;

    if (
      (ultimateWeapons.GOLDEN_TOWER && gtStartTime < currentWaveTime) ||
      // use the checkbox state rather than its unlock status, since it doesn't give coins without research
      (blackHoleUnlocked && bhStartTime < currentWaveTime)
    ) {
      // calculate coins from GH, BH, both solo and when they overlap
      // super inefficient way, we can do better, but get some result
      let secondsGtActive = 0;
      let secondsBhActive = 0;
      let secondsBothActive = 0;
      let secondsNeitherActive = 0;
      for (let i = 0; i < currentWaveTime; i += 1) {
        const bhActive =
          blackHoleUnlocked &&
          ((i >= bhStartTime && i < bhStopTime) ||
            (i >= bhRestartTime && i < bhRestartStopTime));
        const gtActive =
          ultimateWeapons.GOLDEN_TOWER && i >= gtStartTime && i < gtStopTime;
        if (bhActive && gtActive) secondsBothActive += 1;
        else if (bhActive) secondsBhActive += 1;
        else if (gtActive) secondsGtActive += 1;
        else secondsNeitherActive += 1;
      }
      const gtPercent = secondsGtActive / currentWaveTime;
      const bhPercent = secondsBhActive / currentWaveTime;
      const bothPercent = secondsBothActive / currentWaveTime;
      const neitherPercent = secondsNeitherActive / currentWaveTime;

      coinsFromNeither = Math.max(
        scaledBaseCoinsForWave * neitherPercent +
          scaledBaseCoinsForWave * bhPercent * (1 - blackHoleCoverage / 100),
        0,
      );

      coinsFromGoldenTower = Math.max(
        scaledBaseCoinsForWave * goldenTowerBonus * gtPercent,
        0,
      );

      coinsFromBlackHole = Math.max(
        scaledBaseCoinsForWave *
          blackHoleCoins *
          bhPercent *
          (blackHoleCoverage / 100),
        0,
      );

      // overlapping multipliers
      coinsFromBoth = Math.max(
        scaledBaseCoinsForWave *
          bothPercent *
          goldenTowerBonus *
          blackHoleCoins *
          (blackHoleCoverage / 100) +
          scaledBaseCoinsForWave *
            bothPercent *
            goldenTowerBonus *
            (1 - blackHoleCoverage / 100),
        0,
      );
    }

    // CPW contribution scaled for perks
    const coinsPerWaveWithPerks = Math.floor(coinsPerWave * perkCoinMultiplier);

    // Total the contributions
    let waveCoins = Math.floor(
      coinsFromNeither +
        coinsFromGoldenTower +
        coinsFromBlackHole +
        coinsFromBoth +
        coinsPerWaveWithPerks,
    );
    if (isIntroSprinting) waveCoins *= 0.2;
    totalCoins += waveCoins;
    previousCoins = waveCoins;

    const realWaveTime = getRealWaveTime(
      isBossWave,
      gameSpeed,
      standardResearch,
      perks[StandardPerk.INCREASE_GAME_SPEED],
      isIntroSprinting,
      waveAccelerator,
    );

    timers.total += realWaveTime;

    if (currentWave % 100 === 0) {
      const cph = Math.floor((totalCoins * 3600) / timers.total);
      if (verbose) {
        log.push(
          `Stats for ${currentWave}: Coins ${formatCoins(
            totalCoins,
          )}, CPH ${formatCoins(
            cph,
          )}, perk bonus: ${perkCoinMultiplier}, currentGameSpeed: ${currentGameSpeed}`,
        );
      }
      stats.waves[currentWave] = {
        coins: totalCoins,
        time: Math.floor(timers.total),
        cph,
        waveSkips,
        perkMultiplier: perkCoinMultiplier,
        hasTradeoffCoins: stats.perkWaveStats.tradeoffCoinWave.has,
        hasDamageTradeoff: stats.perkWaveStats.damageDamageWave.has,
        hasAllCoinBonuses: stats.perkWaveStats.coinBonusesWave.has,
        hasAllNeededPerks: stats.perkWaveStats.allNeededPerks.has,
        hasGoldenTower: stats.perkWaveStats.goldenTowerWave.has,
        hasGameSpeed: stats.perkWaveStats.gameSpeedWave.has,
        extraOrbs: perks[StandardPerk.ORBS],
        defenseBonuses: perks[StandardPerk.DEFENSE_PERCENT],
        hpBonuses: perks[StandardPerk.MAX_HEALTH],
        basicCoinBonuses: perks[StandardPerk.COIN_BONUSES],
        noMoreOptions: stats.perkWaveStats.noMoreOptions.has,
        hasFirstPerkReduction: stats.perkWaveStats.firstPerkReduction.has,
        hasSecondPerkReduction: stats.perkWaveStats.secondPerkReduction.has,
        hasThirdPerkReduction: stats.perkWaveStats.thirdPerkReduction.has,
        perkCount,
      };
    }
    if (timers.goldenTowerActive > 0)
      timers.goldenTowerActive = Math.max(
        timers.goldenTowerActive - currentWaveTime,
        0,
      );
    if (timers.goldenTowerCooldown < currentWaveTime) {
      const timeUntilGt = currentWaveTime - timers.goldenTowerCooldown;
      timers.goldenTowerActive = Math.max(goldenTowerDuration - timeUntilGt, 0);
      timers.goldenTowerCooldown = goldenTowerCooldown - timeUntilGt;
    } else {
      timers.goldenTowerCooldown -= currentWaveTime;
    }
    if (timers.blackHoleActive > 0)
      timers.blackHoleActive = Math.max(
        timers.blackHoleActive - currentWaveTime,
        0,
      );
    if (timers.blackHoleCooldown < currentWaveTime) {
      const timeUntilBh = currentWaveTime - timers.blackHoleCooldown;
      timers.blackHoleActive = Math.max(blackHoleDuration - timeUntilBh, 0);
      timers.blackHoleCooldown = blackHoleCooldown - timeUntilBh;
    } else {
      timers.blackHoleCooldown -= currentWaveTime;
    }
  }
  stats.finalStats.perkMultiplier = perkCoinMultiplier;
  stats.finalStats.basicCoinBonuses = perks[StandardPerk.COIN_BONUSES];
  stats.finalStats.hpBonuses = perks[StandardPerk.MAX_HEALTH];
  stats.finalStats.defenseBonuses = perks[StandardPerk.DEFENSE_PERCENT];
  stats.finalStats.extraOrbs = perks[StandardPerk.ORBS];
  stats.finalStats.perkCount = perkCount;
  stats.finalStats.perkMultiplier = perkCoinMultiplier;
  if (!stats.waves[currentWave]) {
    const cph = Math.floor((totalCoins * 3600) / timers.total);
    stats.waves[currentWave] = {
      coins: totalCoins,
      time: Math.floor(timers.total),
      cph,
      waveSkips,
      perkMultiplier: perkCoinMultiplier,
      hasTradeoffCoins: stats.perkWaveStats.tradeoffCoinWave.has,
      hasDamageTradeoff: stats.perkWaveStats.damageDamageWave.has,
      hasAllCoinBonuses: stats.perkWaveStats.coinBonusesWave.has,
      hasAllNeededPerks: stats.perkWaveStats.allNeededPerks.has,
      hasGoldenTower: stats.perkWaveStats.goldenTowerWave.has,
      hasGameSpeed: stats.perkWaveStats.gameSpeedWave.has,
      extraOrbs: perks[StandardPerk.ORBS],
      defenseBonuses: perks[StandardPerk.DEFENSE_PERCENT],
      hpBonuses: perks[StandardPerk.MAX_HEALTH],
      basicCoinBonuses: perks[StandardPerk.COIN_BONUSES],
      noMoreOptions: stats.perkWaveStats.noMoreOptions.has,
      hasFirstPerkReduction: stats.perkWaveStats.firstPerkReduction.has,
      hasSecondPerkReduction: stats.perkWaveStats.secondPerkReduction.has,
      hasThirdPerkReduction: stats.perkWaveStats.thirdPerkReduction.has,
      perkCount,
    };
  }
  if (verbose) {
    log.push(`Ended simulation after: ${currentWave}`);
    log.push(`----------------------------------`);
    log.push(`Stats: ${JSON.stringify(stats, null, 2)}`);
    log.unshift(`----------------------------------`);
    const finalPerks = Object.keys(perks).reduce((acc: Quantity, perk: any) => {
      perk as PerkTypes;
      acc[formatPerk(perk, standardResearch, tradeoffResearch)] = perks[perk];
      return acc;
    }, {} as Quantity);
    log.unshift(`Final perks: ${JSON.stringify(finalPerks, null, 2)}`);
  }

  return { stats, log };
};
